import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDeck } from "../../../lib/deck-context";
import { Card, Eyebrow, Reveal, Section, SectionHeader } from "../../deck/primitives";
import { SRC } from "./data";
import { Stat } from "./ui";
import { TabOverview, TabRetail } from "./TabOverview";
import { TabFood, TabEntertainment } from "./TabFoodEnt";
import { TabComps } from "./TabComps";
import { TabLeaseUp, TabSupply } from "./TabLeaseUp";
import { TabUnderwriting } from "./TabUnderwriting";

const TABS = [
  ["overview", "Market Overview", TabOverview], ["retail", "Retail", TabRetail], ["food", "Food + QSR", TabFood],
  ["entertainment", "Entertainment", TabEntertainment], ["comps", "Rent Comps", TabComps], ["absorption", "Absorption + Lease-Up", TabLeaseUp],
  ["supply", "Competitive Supply", TabSupply], ["underwriting", "Project Underwriting", TabUnderwriting],
];

const SIGNALS = [["Occupancy", "Supported"], ["$36/SF Weighted Rent", "Validation Required"], ["Food / QSR Demand", "Positive Market Signal"], ["Entertainment Demand", "Positive Regional Signal"], ["Leasing Risk", "Moderate — Pre-Leasing Required"]];

const Takeaway = () => (
  <Reveal>
    <div data-testid="mk-takeaway" className="mt-14 rounded-[18px] bg-ink-deep text-white p-9 lg:p-12">
      <div className="font-mono text-[11px] tracking-[0.3em] text-arq-mid">INVESTOR TAKEAWAY</div>
      <div className="mt-4 font-display text-3xl lg:text-4xl">Why the Market Supports Further Diligence</div>
      <p className="mt-6 text-[15px] lg:text-base text-white/80 leading-relaxed max-w-5xl">
        The Murrieta Family Commons site benefits from a relatively affluent immediate trade area, mid-90% local retail occupancy, strong small-shop and new-construction asking rents, and significant nearby leasing activity. The adjacent Downtown Marketplace is currently marketing new downtown commercial space at approximately $48/SF/year NNN, compared with the project's $36/SF weighted underwriting assumption. This supports further leasing diligence, although actual achievable rent must still be validated by tenant category and broker feedback.
      </p>
      <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {SIGNALS.map(([k, v], i) => (
          <div key={k} data-testid={`mk-signal-${i}`} className={`rounded-xl border p-5 ${i === 1 || i === 4 ? "border-white/20" : "border-arq-mid/50 bg-arq/20"}`}>
            <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/60">{k}</div>
            <div className="mt-2 text-[15px]">{v}</div>
          </div>
        ))}
      </div>
    </div>
  </Reveal>
);

export default function Market() {
  const { inputs } = useDeck();
  const [tab, setTab] = useState("overview");
  const Current = TABS.find((t) => t[0] === tab)[2];
  const bps = Math.round((95.4 - inputs.occ) * 100);
  return (
    <Section testid="section-market">
      <SectionHeader eyebrow="Market & Leasing" title="South Riverside / Murrieta Market Demand" lede="Retail, Food, Quick-Service & Experiential Leasing Fundamentals" />
      <Reveal>
        <p data-testid="mk-thesis" className="max-w-5xl font-display text-xl md:text-2xl leading-[1.45] text-ink">
          Murrieta Family Commons is being underwritten within a relatively tight South Riverside retail market. Local Murrieta retail occupancy is in the mid-90% range, quality shopping centers are generally well leased, and food, service, fitness and experiential tenants remain important drivers of retail leasing activity. Arqitek's project underwriting intentionally assumes occupancy below current local-market performance.
        </p>
      </Reveal>
      <div className="mt-10 grid sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <Stat testid="mk-card-occupancy" label="Murrieta 2–4 Star retail occupancy" value="95.4%" sub="4.6% vacancy" tag="market" src={SRC.murrieta} type="Occupancy" />
        <Stat testid="mk-card-rent" label="Murrieta asking rent" value="$30.62" sub="/SF/yr · ~$2.55/SF/mo" tag="avg" src={SRC.murrieta} type="Asking rent" />
        <Stat testid="mk-card-leasing" label="Local 12-month leasing activity" value="~521K SF" sub="Leasing activity — not net absorption" tag="local" src={SRC.murrieta} type="Leasing activity" />
        <Stat testid="mk-card-months" label="Average months on market" value="~11.3" sub="months" tag="local" src={SRC.murrieta} type="Months on market" />
        <Stat testid="mk-card-project-occ" label="Project stabilized occupancy" value={`${inputs.occ}%`} sub="Session model" tag="uw" accent src={SRC.arq} type="Underwriting" />
      </div>
      <Card data-testid="mk-bps-callout" className="mt-4 p-6 flex flex-wrap items-center gap-5 border-arq/30">
        <span className="font-display text-4xl text-arq tabular-nums">{bps >= 0 ? `~${bps}` : `+${-bps}`} bps</span>
        <span className="text-[15px] text-char">
          {bps >= 0 ? `Project underwriting is approximately ${bps} basis points below the current 95.4% Murrieta retail occupancy benchmark.` : `Project underwriting is ${-bps} basis points above the 95.4% Murrieta benchmark — no longer conservative.`}
        </span>
      </Card>

      <div data-testid="mk-tabs" className="mt-12 sticky top-16 z-20 -mx-2 px-2 py-3 bg-paper/90 backdrop-blur-xl">
        <Eyebrow className="mb-3">Explore the market</Eyebrow>
        <div className="flex gap-1 overflow-x-auto rounded-full border border-line bg-white p-1 w-fit max-w-full">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`mk-tab-${k}`} onClick={() => setTab(k)} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors duration-200 ${tab === k ? "bg-ink text-white" : "text-char hover:bg-paper"}`}>{l}</button>
          ))}
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={tab} data-testid="mk-tab-content" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="mt-6">
          <Current />
        </motion.div>
      </AnimatePresence>
      <Takeaway />
    </Section>
  );
}
